import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Search, ArrowLeft, ChevronRight, Snowflake, Factory, CreditCard, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAISES = ['ARGENTINA', 'BRAZIL', 'URUGUAY'];

const Frigorificos = () => {
  const [frigorificos, setFrigorificos] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [paisFilter, setPaisFilter] = useState('all');

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formNombre, setFormNombre] = useState('');
  const [formPais, setFormPais] = useState('ARGENTINA');
  const [formPlantas, setFormPlantas] = useState([]);
  const [formPaymentTerms, setFormPaymentTerms] = useState([]);
  const [newPlanta, setNewPlanta] = useState('');
  const [newPaymentTerm, setNewPaymentTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Detail view
  const [selectedFrigo, setSelectedFrigo] = useState(null);

  useEffect(() => { loadFrigorificos(); }, []);
  useEffect(() => { applyFilters(); }, [frigorificos, searchTerm, paisFilter]);

  const loadFrigorificos = async () => {
    try {
      const res = await axios.get(`${API}/frigorificos`);
      setFrigorificos(res.data);
    } catch (error) {
      toast.error('Error al cargar frigoríficos');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let f = [...frigorificos];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      f = f.filter(fr => fr.nombre.toLowerCase().includes(term));
    }
    if (paisFilter !== 'all') {
      f = f.filter(fr => fr.pais === paisFilter);
    }
    setFiltered(f);
  };

  const handleOpenDialog = (frigo = null) => {
    if (frigo) {
      setEditingId(frigo.id);
      setFormNombre(frigo.nombre);
      setFormPais(frigo.pais);
      setFormPlantas(frigo.plantas?.map(p => p.numero) || []);
      setFormPaymentTerms(frigo.paymentTerms || []);
    } else {
      setEditingId(null);
      setFormNombre('');
      setFormPais('ARGENTINA');
      setFormPlantas([]);
      setFormPaymentTerms([]);
    }
    setNewPlanta('');
    setNewPaymentTerm('');
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formNombre.trim()) { toast.error('El nombre es requerido'); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: formNombre.trim(),
        pais: formPais,
        plantas: formPlantas.map(n => ({ numero: n })),
        paymentTerms: formPaymentTerms,
      };
      if (editingId) {
        await axios.put(`${API}/frigorificos/${editingId}`, payload);
        toast.success('Frigorífico actualizado');
      } else {
        await axios.post(`${API}/frigorificos`, payload);
        toast.success('Frigorífico creado');
      }
      setShowDialog(false);
      loadFrigorificos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/frigorificos/${id}`);
      toast.success('Frigorífico eliminado');
      loadFrigorificos();
      if (selectedFrigo?.id === id) setSelectedFrigo(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
    setDeleteId(null);
  };

  const addPlanta = () => {
    const num = parseInt(newPlanta);
    if (!isNaN(num) && num > 0 && !formPlantas.includes(num)) {
      setFormPlantas([...formPlantas, num]);
      setNewPlanta('');
    }
  };

  const removePlanta = (idx) => {
    setFormPlantas(formPlantas.filter((_, i) => i !== idx));
  };

  const addPaymentTerm = () => {
    if (newPaymentTerm.trim() && !formPaymentTerms.includes(newPaymentTerm.trim())) {
      setFormPaymentTerms([...formPaymentTerms, newPaymentTerm.trim()]);
      setNewPaymentTerm('');
    }
  };

  const removePaymentTerm = (idx) => {
    setFormPaymentTerms(formPaymentTerms.filter((_, i) => i !== idx));
  };

  const paisColor = (pais) => {
    if (pais === 'ARGENTINA') return 'bg-sky-500/15 text-sky-700 dark:text-sky-400';
    if (pais === 'BRAZIL') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400';
    if (pais === 'URUGUAY') return 'bg-violet-500/15 text-violet-700 dark:text-violet-400';
    return '';
  };

  if (loading) {
    return (<div className="flex items-center justify-center min-h-[400px]"><div className="text-muted-foreground">Cargando...</div></div>);
  }

  // ========== DETAIL VIEW ==========
  if (selectedFrigo) {
    return (
      <div className="space-y-6 bg-background min-h-screen">
        <div className="bg-background border-b">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
            <Button variant="ghost" className="mb-4" onClick={() => setSelectedFrigo(null)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Volver
            </Button>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg"><Snowflake className="h-6 w-6 text-primary" /></div>
                <div>
                  <h1 className="text-3xl font-semibold tracking-tight">{selectedFrigo.nombre}</h1>
                  <Badge className={`mt-1 ${paisColor(selectedFrigo.pais)}`}>{selectedFrigo.pais}</Badge>
                </div>
              </div>
              <Button onClick={() => handleOpenDialog(selectedFrigo)}><Edit className="h-4 w-4 mr-2" /> Editar</Button>
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-6 md:grid-cols-2">
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Factory className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Plantas ({selectedFrigo.plantas?.length || 0})</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {(selectedFrigo.plantas || []).map((p, i) => (
                <Badge key={i} variant="outline" className="text-sm px-3 py-1.5">#{p.numero}</Badge>
              ))}
              {(!selectedFrigo.plantas || selectedFrigo.plantas.length === 0) && (
                <p className="text-sm text-muted-foreground">Sin plantas registradas</p>
              )}
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Formas de Pago ({selectedFrigo.paymentTerms?.length || 0})</h2>
            </div>
            <div className="flex flex-col gap-2">
              {(selectedFrigo.paymentTerms || []).map((pt, i) => (
                <Badge key={i} variant="secondary" className="text-sm px-3 py-1.5 w-fit">{pt}</Badge>
              ))}
              {(!selectedFrigo.paymentTerms || selectedFrigo.paymentTerms.length === 0) && (
                <p className="text-sm text-muted-foreground">Sin formas de pago registradas</p>
              )}
            </div>
          </Card>
        </div>

      {/* Dialogs also need to render in detail view */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar' : 'Nuevo'} Frigorífico</DialogTitle>
            <DialogDescription>{editingId ? 'Modifica los datos del frigorífico' : 'Ingrese los datos del nuevo frigorífico'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={formNombre} onChange={(e) => setFormNombre(e.target.value)} placeholder="Ej. MINERVA UY" autoFocus />
            </div>
            <div className="space-y-2">
              <Label>País *</Label>
              <Select value={formPais} onValueChange={setFormPais}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAISES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Plantas</Label>
              <div className="flex gap-2">
                <Input type="number" value={newPlanta} onChange={(e) => setNewPlanta(e.target.value)} placeholder="Nro. planta" onKeyDown={(e) => e.key === 'Enter' && addPlanta()} />
                <Button type="button" variant="outline" onClick={addPlanta} size="sm"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formPlantas.map((num, i) => (
                  <Badge key={i} variant="outline" className="px-3 py-1 gap-1">
                    #{num}
                    <button onClick={() => removePlanta(i)} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Formas de Pago</Label>
              <div className="flex gap-2">
                <Input value={newPaymentTerm} onChange={(e) => setNewPaymentTerm(e.target.value)} placeholder="Ej. 40 AG PI 60 AG DOCS" onKeyDown={(e) => e.key === 'Enter' && addPaymentTerm()} />
                <Button type="button" variant="outline" onClick={addPaymentTerm} size="sm"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-col gap-1.5 mt-2">
                {formPaymentTerms.map((pt, i) => (
                  <Badge key={i} variant="secondary" className="px-3 py-1 gap-1 w-fit">
                    {pt}
                    <button onClick={() => removePaymentTerm(i)} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar frigorífico?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    );
  }

  // ========== MAIN LIST ==========
  return (
    <div className="space-y-6 bg-background min-h-screen">
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Frigoríficos</h1>
              <p className="text-muted-foreground mt-1">{frigorificos.length} frigoríficos registrados</p>
            </div>
            <Button onClick={() => handleOpenDialog()} data-testid="new-frigorifico-button">
              <Plus className="h-4 w-4 mr-2" /> Nuevo Frigorífico
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar frigorífico..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
            </div>
            <Select value={paisFilter} onValueChange={setPaisFilter}>
              <SelectTrigger className="w-[180px]"><SelectValue placeholder="País" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los países</SelectItem>
                {PAISES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {filtered.length === 0 ? (
          <Card className="p-12 text-center">
            <Snowflake className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{frigorificos.length === 0 ? 'No hay frigoríficos creados' : 'No se encontraron resultados'}</p>
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((frigo) => (
              <Card key={frigo.id} className="p-5 cursor-pointer hover:border-primary/50 transition-colors group" onClick={() => setSelectedFrigo(frigo)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors shrink-0">
                      <Snowflake className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate">{frigo.nombre}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`text-xs ${paisColor(frigo.pais)}`}>{frigo.pais}</Badge>
                        <span className="text-xs text-muted-foreground">{frigo.plantas?.length || 0} planta(s)</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenDialog(frigo); }} className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDeleteId(frigo.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <ChevronRight className="h-5 w-5 text-muted-foreground ml-1" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar' : 'Nuevo'} Frigorífico</DialogTitle>
            <DialogDescription>{editingId ? 'Modifica los datos del frigorífico' : 'Ingrese los datos del nuevo frigorífico'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={formNombre} onChange={(e) => setFormNombre(e.target.value)} placeholder="Ej. MINERVA UY" autoFocus />
            </div>
            <div className="space-y-2">
              <Label>País *</Label>
              <Select value={formPais} onValueChange={setFormPais}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAISES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Plantas</Label>
              <div className="flex gap-2">
                <Input type="number" value={newPlanta} onChange={(e) => setNewPlanta(e.target.value)} placeholder="Nro. planta" onKeyDown={(e) => e.key === 'Enter' && addPlanta()} />
                <Button type="button" variant="outline" onClick={addPlanta} size="sm"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formPlantas.map((num, i) => (
                  <Badge key={i} variant="outline" className="px-3 py-1 gap-1">
                    #{num}
                    <button onClick={() => removePlanta(i)} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Formas de Pago</Label>
              <div className="flex gap-2">
                <Input value={newPaymentTerm} onChange={(e) => setNewPaymentTerm(e.target.value)} placeholder="Ej. 40 AG PI 60 AG DOCS" onKeyDown={(e) => e.key === 'Enter' && addPaymentTerm()} />
                <Button type="button" variant="outline" onClick={addPaymentTerm} size="sm"><Plus className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-col gap-1.5 mt-2">
                {formPaymentTerms.map((pt, i) => (
                  <Badge key={i} variant="secondary" className="px-3 py-1 gap-1 w-fit">
                    {pt}
                    <button onClick={() => removePaymentTerm(i)} className="ml-1 hover:text-destructive"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar frigorífico?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Frigorificos;
